import React, { useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/template-api';

import { useFormState } from '../../../../hooks/useFormState';

import { defaultErrorMessage } from '../../../../constants/errorMessages';
import { collectionColorOptions } from '../../../../constants/collections';

import Modal from '../../../../design-system/Modal/Modal';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ColorPicker from '../../../../design-system/ColorPicker/ColorPicker';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import { Button, Input } from '../../../../design-system';

const LabelEditorModal = ({
    mode = 'create',
    data,
    allLabels,
    setLabels,
    shouldSaveChanges = false,
    playbookTemplateId,
    onClose,
}) => {
    const { formData, handleInputChange } = useFormState(() => {
        if (mode === 'edit' && data) {
            const { name, icon_color } = data;
            return { name, icon_color };
        }
        return { name: '', icon_color: collectionColorOptions[0] };
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const allRequiredFieldsFilled = formData.name?.trim() && formData.icon_color;

    const getRequestBody = () => {
        const { name, icon_color } = formData;
        const formattedLabel = { name, icon_color };

        let newLabels = allLabels || [];

        if (mode === 'create') {
            newLabels = [...allLabels, formattedLabel];
        }

        if (mode === 'edit') {
            newLabels = allLabels.map((item, index) =>
                data.index === index ? { ...item, ...formattedLabel } : item
            );
        }

        return { labels: newLabels };
    };

    const handleSubmit = async () => {
        if (!allRequiredFieldsFilled) return;

        try {
            setIsLoading(true);

            const { labels } = getRequestBody();
            if (!shouldSaveChanges) {
                setLabels(labels);
            }

            if (shouldSaveChanges) {
                const { data: response } = await client.patch(
                    API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
                    { labels }
                );

                setLabels(response.labels);
            }

            onClose();
        } catch (e) {
            setIsLoading(false);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    const submitButtonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled
        ? 'default'
        : 'disabled';

    const title = {
        create: 'Add New Label',
        edit: 'Edit Label',
    }[mode];

    const submitButtonText = {
        create: 'Add',
        edit: 'Save',
    }[mode];

    return (
        <Modal size="midSize" onClose={onClose} resetPadding>
            <div className="p-6 lg:p-8 flex flex-col gap-6 lg:gap-8">
                <ModalHeader title={title} onClose={onClose} />

                <div className="flex flex-col gap-4">
                    <Input
                        size="xs"
                        value={formData.name}
                        name="label_name"
                        label="Label Name"
                        isRequired
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Give this label a unique name"
                    />

                    <ColorPicker
                        label="Label color"
                        options={collectionColorOptions}
                        selected={formData.icon_color}
                        handleSelect={(color) => handleInputChange('icon_color', color)}
                    />
                </div>

                <div className="flex items-center justify-between gap-3">
                    <Button type="ghost" size="sm" text="Cancel" onClick={onClose} />
                    <Button
                        type="secondary"
                        size="sm"
                        text={submitButtonText}
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default LabelEditorModal;
